import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    useMediaQuery,
    List,
    ListItemButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import EventsApi from '../../api/eventsApi/eventsApi';
import ApiBase from '../../api/apiBase';
import { reportUi } from './reportUiTheme';
import { FACILITY_SECTOR_LABELS } from './facilitySectors';

/** Şablon (v1 HTML): alanın hemen üstünde, küçük all-caps başlık */
const FIELD_LABEL_SX = {
    display: 'block',
    fontSize: 11,
    letterSpacing: '0.14em',
    color: reportUi.textMuted,
    fontWeight: 600,
    mb: 0.75,
    lineHeight: 1.25,
    textTransform: 'uppercase',
} as const;

const PRIMARY = '#094151';
const PRIMARY_DARK = '#062a35';
/** Tesis detay / denetim footer ile aynı palet (reportpages.tsx) */
const FOOTER_BG = '#F2F7F9';
const FOOTER_BORDER = '#C8DDE4';
const FOOTER_PRIMARY = '#0D3D4A';
const FOOTER_PRIMARY_HOVER = '#134F60';

const PRESET_RADII = [5000, 10000, 15000] as const;

/** Kontrol listesi arama + Bulgu metin alanları ile uyumlu çerçeve */
const FACILITY_FIELD_OUTLINE = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        bgcolor: '#fff',
        minHeight: 52,
        fontSize: 16,
        '& fieldset': { borderWidth: '2px', borderColor: '#C8DDE4' },
        '&:hover fieldset': { borderColor: '#1A6070' },
        '&.Mui-focused fieldset': { borderColor: '#1A6070', borderWidth: '2px' },
    },
} as const;

const facilityTextFieldSx = {
    ...FACILITY_FIELD_OUTLINE,
    '& .MuiOutlinedInput-input::placeholder': {
        color: '#5A7A84',
        opacity: 1,
    },
} as const;

const facilitySelectSx = {
    borderRadius: '12px',
    minHeight: 52,
    bgcolor: '#fff',
    fontSize: 16,
    '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: '#C8DDE4',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1A6070',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: '#1A6070',
    },
} as const;

interface QuestionCategoryResponse {
    id: string;
    name: string;
    userId: string;
    questionTypeId: string;
    pinId: string;
}

async function reverseGeocode(lat: number, lon: number): Promise<{ line1: string; line2: string }> {
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=tr`,
            { headers: { Accept: 'application/json' } }
        );
        const data = await res.json();
        const addr = data?.address || {};
        const district = addr.suburb || addr.neighbourhood || addr.city_district || addr.town || '';
        const city = addr.city || addr.province || addr.state || '';
        const road = addr.road || '';
        const house = addr.house_number ? `${addr.house_number} ` : '';
        const line1 = data?.display_name
            ? String(data.display_name).split(',').slice(0, 2).join(',').trim()
            : [house + road, district].filter(Boolean).join(', ') || `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
        const line2 = [city].filter(Boolean).join(' · ') || 'Türkiye';
        return { line1, line2 };
    } catch {
        return {
            line1: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
            line2: 'Adres çözümlenemedi',
        };
    }
}

function accuracyToPercent(accuracyM: number | null): number {
    if (accuracyM == null || !Number.isFinite(accuracyM)) return 95;
    const p = Math.round(100 - Math.min(accuracyM / 8, 25));
    return Math.min(99, Math.max(70, p));
}

/**
 * Yeni tesis formu — yerleşim yalnızca bu dosyada: kök `100vh` flex kolon (üst bar + scroll gövde + footer).
 * Form alanı `main` genişliğine yayılır; tablet/mobilde dış yatay gutter yok (beyaz yüzey kenara kadar).
 * Orta kolon flex ile header–footer arasını doldurur; `Paper` en az bu alan kadar uzar (altta boş gri şerit kalmaz).
 * Global `PageWrapper` bu sayfaya özel kural içermez; taşan içerik yalnızca orta bölümde kayar.
 */
const AddFacilityPage: React.FC = () => {
    const navigate = useNavigate();
    // Reports.tsx / reportpages.tsx ile aynı tablet aralığı
    const isTablet = useMediaQuery('(min-width:600px) and (max-width:1199.95px)');
    const isMobile = useMediaQuery('(max-width:599.95px)');
    const isDesktop = !isTablet && !isMobile;
    const userId = useSelector((state: RootState) => state.auth.id) ?? '';

    const [facilityName, setFacilityName] = useState('');
    const [sectorKey, setSectorKey] = useState<string>('');
    const [sectorOther, setSectorOther] = useState('');
    const [facilityType, setFacilityType] = useState('');
    const [employeeCount, setEmployeeCount] = useState('');

    const [addressQuery, setAddressQuery] = useState('');
    const [addressSuggestions, setAddressSuggestions] = useState<
        { lat: string; lon: string; display_name: string }[]
    >([]);
    const [addressSearchLoading, setAddressSearchLoading] = useState(false);
    const [addressSearchError, setAddressSearchError] = useState<string | null>(null);

    const [radiusOption, setRadiusOption] = useState<string>('5000');
    const [customRadius, setCustomRadius] = useState('');

    const [lat, setLat] = useState<number | null>(null);
    const [lon, setLon] = useState<number | null>(null);
    const [locationLine1, setLocationLine1] = useState('');
    const [locationLine2, setLocationLine2] = useState('');
    const [accuracyPct, setAccuracyPct] = useState<number | null>(null);
    const [gpsLoading, setGpsLoading] = useState(false);
    const [gpsError, setGpsError] = useState<string | null>(null);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const radiusMeters = useMemo(() => {
        if (radiusOption === 'custom') {
            const n = Number(customRadius);
            return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
        }
        return Number(radiusOption) || 0;
    }, [radiusOption, customRadius]);

    const handleRadiusOptionChange = (value: string) => {
        setRadiusOption(value);
        if (value === 'custom') {
            setCustomRadius((prev) => prev || '');
            return;
        }
        setCustomRadius('');
    };

    const handleAcquireLocation = useCallback(() => {
        setGpsError(null);
        if (!navigator.geolocation) {
            setGpsError('Tarayıcınız konum servisini desteklemiyor.');
            return;
        }
        setGpsLoading(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const latitude = pos.coords.latitude;
                const longitude = pos.coords.longitude;
                setLat(latitude);
                setLon(longitude);
                setAccuracyPct(accuracyToPercent(pos.coords.accuracy));
                const geo = await reverseGeocode(latitude, longitude);
                setLocationLine1(geo.line1);
                setLocationLine2(geo.line2);
                setGpsLoading(false);
            },
            (err) => {
                setGpsLoading(false);
                if (err.code === 1) {
                    setGpsError('Konum izni reddedildi. Lütfen tarayıcı ayarlarından izin verin.');
                } else {
                    setGpsError('Konum alınamadı. Tekrar deneyin.');
                }
            },
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
    }, []);

    const isValidCoordinates = (input: string): boolean => {
        const coordPattern = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;
        if (!coordPattern.test(input.trim())) return false;
        const [la, ln] = input.split(',').map((c) => parseFloat(c.trim()));
        return la >= -90 && la <= 90 && ln >= -180 && ln <= 180;
    };

    const handleAddressSearch = useCallback(async () => {
        setAddressSearchError(null);
        const q = addressQuery.trim();
        if (!q) {
            setAddressSearchError('Adres veya koordinat girin.');
            return;
        }
        if (isValidCoordinates(q)) {
            const [la, ln] = q.split(',').map((c) => parseFloat(c.trim()));
            setLat(la);
            setLon(ln);
            setAccuracyPct(null);
            const geo = await reverseGeocode(la, ln);
            setLocationLine1(geo.line1);
            setLocationLine2(geo.line2);
            setAddressSuggestions([]);
            return;
        }
        setAddressSearchLoading(true);
        setAddressSuggestions([]);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&addressdetails=1&accept-language=tr`,
                { headers: { Accept: 'application/json' } }
            );
            const data = await res.json();
            if (Array.isArray(data) && data.length) {
                setAddressSuggestions(data);
            } else {
                setAddressSearchError('Sonuç bulunamadı. Farklı bir ifade deneyin.');
            }
        } catch {
            setAddressSearchError('Adres araması başarısız.');
        } finally {
            setAddressSearchLoading(false);
        }
    }, [addressQuery]);

    const handlePickAddressSuggestion = useCallback(
        async (s: { lat: string; lon: string; display_name: string }) => {
            const latitude = parseFloat(s.lat);
            const longitude = parseFloat(s.lon);
            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
            setLat(latitude);
            setLon(longitude);
            setAccuracyPct(null);
            const line1 = s.display_name.split(',').slice(0, 2).join(',').trim() || s.display_name;
            setLocationLine1(line1);
            setAddressSuggestions([]);
            const geo = await reverseGeocode(latitude, longitude);
            setLocationLine2(geo.line2);
            setAddressQuery('');
        },
        []
    );

    const checkDuplicateCoordinates = async (latitude: number, longitude: number): Promise<boolean> => {
        try {
            const existingPins = await EventsApi.getPins();
            if (existingPins && Array.isArray(existingPins)) {
                return existingPins.some(
                    (pin) =>
                        Math.abs(pin.latitude - latitude) < 0.0001 &&
                        Math.abs(pin.longitude - longitude) < 0.0001
                );
            }
            return false;
        } catch {
            return false;
        }
    };

    const handleSubmit = async () => {
        setSubmitError(null);
        if (lat == null || lon == null) {
            setSubmitError('Konumu GPS ile alın veya adres aramasıyla seçin.');
            return;
        }
        if (!facilityName.trim()) {
            setSubmitError('Tesis adı zorunludur.');
            return;
        }
        if (!sectorKey) {
            setSubmitError('Sektör seçin.');
            return;
        }
        if (sectorKey === 'other' && !sectorOther.trim()) {
            setSubmitError('“Diğer” seçildiğinde sektör adını yazın.');
            return;
        }
        if (radiusMeters <= 0) {
            setSubmitError('Geçerli bir yarıçap (metre) seçin veya girin.');
            return;
        }
        if (!userId) {
            setSubmitError('Oturum bilgisi bulunamadı.');
            return;
        }

        setSubmitting(true);
        try {
            const dup = await checkDuplicateCoordinates(lat, lon);
            if (dup) {
                setSubmitError('Bu konumda kayıtlı bir tesis var. Farklı bir noktadan konum alın.');
                setSubmitting(false);
                return;
            }

            const userData = await EventsApi.getUser();
            const questionsJsonData = {
                wikipediaTitle: locationLine1 || `Koordinat: ${lat.toFixed(6)}, ${lon.toFixed(6)}`,
                wikipediaSummary: null as string | null,
            };
            const jsonBlob = new Blob([JSON.stringify(questionsJsonData)], { type: 'application/json' });
            const jsonFile = new File([jsonBlob], 'questions.json', { type: 'application/json' });

            const formData = new FormData();
            formData.append('Name', facilityName.trim());
            formData.append('Latitude', lat.toString());
            formData.append('Longitude', lon.toString());
            formData.append('RadiusInMeters', String(radiusMeters));
            formData.append('Sector', sectorKey);
            if (sectorKey === 'other') {
                formData.append('SectorCustom', sectorOther.trim());
            }
            if (facilityType.trim()) {
                formData.append('FacilityType', facilityType.trim());
            }
            const emp = employeeCount.trim() ? parseInt(employeeCount.trim(), 10) : NaN;
            if (Number.isFinite(emp) && emp >= 0) {
                formData.append('EmployeeCount', String(emp));
            }
            formData.append('userId', userId);
            formData.append('organizationId', userData.organizationId);
            formData.append('Files', jsonFile);

            const response = await EventsApi.createNewPin(formData);

            if (response?.id) {
                const categoryResponse = await ApiBase.postQuestionPromise<QuestionCategoryResponse>(
                    '/api/QuestionCategories',
                    {
                        name: 'SONUÇ VE DEĞERLENDİRME',
                        userId,
                        questionTypeId: 'd5d3dd45-0a2e-457d-933b-c66a3853469f',
                        pinId: response.id,
                    }
                );

                if (categoryResponse?.id) {
                    await EventsApi.saveQuestion({
                        text: 'Sonuç ve Değerlendirme',
                        userId,
                        questionCategoryId: categoryResponse.id,
                        pinId: response.id,
                    });
                }

                navigate(`/reports/${response.id}`);
            } else {
                setSubmitError('Tesis kaydı oluşturulamadı.');
            }
        } catch {
            setSubmitError('Tesis kaydedilirken bir hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const hasLocation = lat != null && lon != null;

    const headerSubtitle = useMemo(() => {
        const rLabel =
            radiusMeters <= 0
                ? 'Yarıçap aşağıdan seçilir'
                : radiusMeters >= 1000
                  ? `${(radiusMeters / 1000) % 1 === 0 ? radiusMeters / 1000 : (radiusMeters / 1000).toFixed(1)} km yarıçap`
                  : `${radiusMeters} m yarıçap`;
        if (!hasLocation) return `GPS veya adres ile konum seçin · ${rLabel}`;
        const locShort = locationLine2 || 'Konum alındı';
        return `${locShort} · ${rLabel}`;
    }, [hasLocation, locationLine2, radiusMeters]);

    /** Form + footer dışı: mobilde hafif iç boşluk; tablette 0 (kart tam genişlik); masaüstünde geniş kenar */
    const contentEdgePaddingX = isMobile ? 1.5 : isTablet ? 0 : 4;
    const headerPaddingX = isDesktop ? 4 : 2;

    const sectorOk =
        Boolean(sectorKey) && (sectorKey !== 'other' || sectorOther.trim().length > 0);

    const canSubmitForm =
        hasLocation && Boolean(facilityName.trim()) && sectorOk && radiusMeters > 0;

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                minHeight: '100vh',
                height: '100vh',
                maxHeight: '100vh',
                bgcolor: reportUi.pageBg,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            {/* Üst bar: reportpages (Tesis detayları) ile aynı dil */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: headerPaddingX,
                    pt: isDesktop ? 1.5 : 1,
                    pb: 1,
                    flexShrink: 0,
                    bgcolor: reportUi.headerBg,
                    color: '#fff',
                    borderRadius: '0 !important',
                    '& .MuiButton-root': { borderRadius: '0 !important' },
                }}
            >
                <Button
                    onClick={() => navigate('/reports')}
                    variant="contained"
                    size={isDesktop ? 'medium' : 'small'}
                    aria-label="Geri"
                    sx={{
                        minWidth: isTablet ? 48 : 40,
                        borderRadius: 0,
                        bgcolor: 'hsl(0 0% 100% / 0.14)',
                        color: '#FFFFFF',
                        boxShadow: 'none',
                        border: '1px solid hsl(0 0% 100% / 0.25)',
                        '&:hover': { bgcolor: 'hsl(0 0% 100% / 0.2)', boxShadow: 'none' },
                    }}
                >
                    <ArrowBackIcon fontSize={isDesktop ? 'medium' : 'small'} />
                </Button>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            letterSpacing: 2,
                            color: reportUi.accent,
                            fontSize: isDesktop ? 13 : 11,
                            display: 'block',
                        }}
                    >
                        YENİ LOKASYON
                    </Typography>
                    <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
                        <Typography
                            variant={isMobile ? 'h6' : isDesktop ? 'h4' : 'h5'}
                            sx={{ fontWeight: 800, color: '#FFFFFF' }}
                        >
                            Yeni Lokasyon Ekle
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: reportUi.headerSubtext, fontSize: isDesktop ? 15 : 13 }}
                        >
                            {headerSubtitle}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    width: '100%',
                    maxWidth: '100%',
                    px: contentEdgePaddingX,
                    py: 2.5,
                    pb: 2,
                    boxSizing: 'border-box',
                }}
            >
                <Paper
                    elevation={isTablet || isMobile ? 3 : 2}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        borderRadius: isTablet || isMobile ? 4 : 3,
                        p: isMobile ? 2 : 3,
                        boxShadow: isTablet || isMobile ? undefined : '0 4px 24px rgba(0,0,0,0.06)',
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={FIELD_LABEL_SX}>KONUM (GPS)</Typography>
                        <Box
                            onClick={gpsLoading ? undefined : handleAcquireLocation}
                            sx={{
                                borderRadius: 2.5,
                                bgcolor: PRIMARY,
                                color: '#fff',
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                cursor: gpsLoading ? 'wait' : 'pointer',
                                '&:hover': { bgcolor: gpsLoading ? PRIMARY : PRIMARY_DARK },
                            }}
                        >
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            {gpsLoading ? (
                                <CircularProgress size={26} sx={{ color: '#fff' }} />
                            ) : (
                                <PlaceIcon sx={{ color: '#ff6b6b', fontSize: 28 }} />
                            )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {hasLocation ? (
                                <>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.35 }}>
                                        {locationLine1 || `${lat!.toFixed(5)}, ${lon!.toFixed(5)}`}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                                        {locationLine2}
                                        {accuracyPct != null ? ` · %${accuracyPct} doğruluk` : ''}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                        Konumu cihazdan al (GPS)
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5 }}>
                                        Kutuya dokunun veya tıklayın; adres araması yukarıda
                                    </Typography>
                                </>
                            )}
                        </Box>
                        {hasLocation && !gpsLoading && (
                            <Box
                                component="span"
                                sx={{
                                    bgcolor: '#2e7d32',
                                    color: '#fff',
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 10,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    flexShrink: 0,
                                }}
                            >
                                GPS ✓
                            </Box>
                        )}
                        {!hasLocation && !gpsLoading && (
                            <MyLocationIcon sx={{ opacity: 0.85, flexShrink: 0 }} />
                        )}
                        </Box>
                    </Box>

                    {gpsError && (
                        <Typography color="error" variant="body2" sx={{ mb: 2, mt: -1 }}>
                            {gpsError}
                        </Typography>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <Typography sx={FIELD_LABEL_SX}>ADRES VEYA KOORDİNAT</Typography>
                        <Typography variant="body2" sx={{ color: reportUi.textMuted, mb: 1.5 }}>
                            Haritadaki gibi adres yazın veya <code>enlem, boylam</code> girin; Ara deyin, listeden seçin.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <TextField
                                fullWidth
                                sx={{ ...facilityTextFieldSx, flex: 1, minWidth: 200 }}
                                placeholder="Örn. Anıtkabir, Çankaya veya 39.925, 32.836"
                                value={addressQuery}
                                onChange={(e) => {
                                    setAddressQuery(e.target.value);
                                    setAddressSearchError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') void handleAddressSearch();
                                }}
                                disabled={addressSearchLoading}
                                inputProps={{ 'aria-label': 'Adres veya koordinat' }}
                            />
                            <Button
                                variant="contained"
                                disabled={addressSearchLoading}
                                onClick={() => void handleAddressSearch()}
                                sx={{
                                    minHeight: 52,
                                    borderRadius: '12px',
                                    px: 2.5,
                                    bgcolor: PRIMARY,
                                    color: '#fff',
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none', color: '#fff' },
                                }}
                            >
                                {addressSearchLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Ara'}
                            </Button>
                        </Box>
                        {addressSearchError && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                {addressSearchError}
                            </Typography>
                        )}
                        {addressSuggestions.length > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    mt: 1.5,
                                    maxHeight: 240,
                                    overflow: 'auto',
                                    borderRadius: 2,
                                    borderColor: '#C8DDE4',
                                }}
                            >
                                <List dense disablePadding>
                                    {addressSuggestions.map((s, i) => (
                                        <ListItemButton
                                            key={`${s.lat}-${s.lon}-${i}`}
                                            onClick={() => void handlePickAddressSuggestion(s)}
                                        >
                                            <Typography variant="body2">{s.display_name}</Typography>
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography sx={FIELD_LABEL_SX}>TESİS ADI *</Typography>
                        <TextField
                            fullWidth
                            placeholder="Tesis adı"
                            value={facilityName}
                            onChange={(e) => setFacilityName(e.target.value)}
                            required
                            inputProps={{ 'aria-label': 'Tesis adı' }}
                            sx={facilityTextFieldSx}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={FIELD_LABEL_SX}>SEKTÖR *</Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={sectorKey}
                                    onChange={(e) => {
                                        setSectorKey(e.target.value as string);
                                        if (e.target.value !== 'other') setSectorOther('');
                                    }}
                                    displayEmpty
                                    sx={facilitySelectSx}
                                    inputProps={{ 'aria-label': 'Sektör' }}
                                    renderValue={(v) =>
                                        v ? (
                                            FACILITY_SECTOR_LABELS[v] ?? v
                                        ) : (
                                            <Box component="span" sx={{ color: reportUi.textMuted }}>
                                                Seçin
                                            </Box>
                                        )
                                    }
                                >
                                    <MenuItem value="" disabled>
                                        <em>Seçin</em>
                                    </MenuItem>
                                    {Object.entries(FACILITY_SECTOR_LABELS).map(([key, label]) => (
                                        <MenuItem key={key} value={key}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={FIELD_LABEL_SX}>TESİS TÜRÜ (İSTEĞE BAĞLI)</Typography>
                            <TextField
                                fullWidth
                                placeholder="Örn. üretim tesisi, AVM"
                                value={facilityType}
                                onChange={(e) => setFacilityType(e.target.value)}
                                inputProps={{ 'aria-label': 'Tesis türü' }}
                                sx={facilityTextFieldSx}
                            />
                        </Box>
                    </Box>

                    {sectorKey === 'other' && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={FIELD_LABEL_SX}>SEKTÖR (ELLE) *</Typography>
                            <TextField
                                fullWidth
                                placeholder="Sektör adı"
                                value={sectorOther}
                                onChange={(e) => setSectorOther(e.target.value)}
                                inputProps={{ 'aria-label': 'Özel sektör' }}
                                sx={facilityTextFieldSx}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: isMobile ? 'column' : 'row' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={FIELD_LABEL_SX}>YARIÇAP (METRE)</Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={radiusOption}
                                    onChange={(e) => handleRadiusOptionChange(e.target.value as string)}
                                    sx={facilitySelectSx}
                                    inputProps={{ 'aria-label': 'Yarıçap metre' }}
                                >
                                    {PRESET_RADII.map((r) => (
                                        <MenuItem key={r} value={String(r)}>
                                            {r} m
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="custom">Diğer (elle gir)</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={FIELD_LABEL_SX}>ÇALIŞAN</Typography>
                            <TextField
                                fullWidth
                                type="number"
                                placeholder="Kişi"
                                value={employeeCount}
                                onChange={(e) => setEmployeeCount(e.target.value)}
                                inputProps={{ min: 0, 'aria-label': 'Çalışan sayısı' }}
                                sx={facilityTextFieldSx}
                            />
                        </Box>
                    </Box>

                    {radiusOption === 'custom' && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={FIELD_LABEL_SX}>DİĞER YARIÇAP (METRE) *</Typography>
                            <TextField
                                fullWidth
                                type="number"
                                placeholder="Metre cinsinden"
                                value={customRadius}
                                onChange={(e) => setCustomRadius(e.target.value)}
                                inputProps={{ min: 1, step: 1, 'aria-label': 'Özel yarıçap' }}
                                sx={facilityTextFieldSx}
                            />
                        </Box>
                    )}

                    {submitError && (
                        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                            {submitError}
                        </Typography>
                    )}
                    {/* Header–footer arası boşluğu beyaz doldurur; form uzunsa bu kolon scroll olur */}
                    <Box sx={{ flex: 1, minHeight: 0 }} aria-hidden />
                </Paper>
            </Box>

            {/* Footer: reportpages.tsx renderFooterActions ile aynı mantık; bu sayfanın 100vh kolonunda altta sabit */}
            <Box
                sx={{
                    width: '100%',
                    bgcolor: FOOTER_BG,
                    borderTop: `1px solid ${FOOTER_BORDER}`,
                    py: 2,
                    flexShrink: 0,
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        px: contentEdgePaddingX,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                        boxSizing: 'border-box',
                    }}
                >
                    <Button
                        onClick={() => navigate('/reports')}
                        variant="contained"
                        sx={{
                            minWidth: 56,
                            minHeight: 56,
                            borderRadius: 0,
                            bgcolor: 'white',
                            color: FOOTER_PRIMARY,
                            boxShadow: 'none',
                            border: `2px solid ${FOOTER_BORDER}`,
                            '&:hover': {
                                borderColor: FOOTER_PRIMARY,
                                bgcolor: 'rgba(13,61,74,0.04)',
                                boxShadow: 'none',
                            },
                        }}
                        aria-label="Geri"
                    >
                        <ArrowBackIcon />
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={submitting || !canSubmitForm}
                        onClick={handleSubmit}
                        sx={{
                            minHeight: 56,
                            borderRadius: 0,
                            fontWeight: 700,
                            fontSize: 17,
                            textTransform: 'none',
                            backgroundColor: `${FOOTER_PRIMARY} !important`,
                            color: '#fff !important',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: `${FOOTER_PRIMARY_HOVER} !important`,
                                color: '#fff !important',
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'rgba(13, 61, 74, 0.35) !important',
                                color: 'rgba(255,255,255,0.75) !important',
                            },
                        }}
                    >
                        {submitting ? (
                            <CircularProgress size={24} sx={{ color: '#fff' }} />
                        ) : (
                            'Tesis Ekle →'
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default AddFacilityPage;
